import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { IntercompanyResult, IntercompanySnapshot } from "@ranfac/shared";
import { fetchIntercompany } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function Intercompany() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: IntercompanySnapshot;
    result: IntercompanyResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchIntercompany(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { result, source } = data;
  const mirrored = result.crossCompanyMismatch < 1;

  return (
    <>
      <div className="card">
        <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        <h1>Intercompany — Cervos</h1>
        <div className="subtle">
          Period ending {result.periodEnd} · Ranfac ↔ Cervos mirror check ·{" "}
          {source === "upload" ? (
            <span className="badge" style={{ background: "#dcfce7", color: "#166534" }}>
              uploaded files
            </span>
          ) : (
            <span className="badge warn">
              sample data — needs trial-balance upload from both Sage companies
            </span>
          )}
        </div>
        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Sage source</div>
            <div>GL_AccountHistory on 1075/1076/1080/2445 (Ranfac) and 1050/1075/2440/2445/2446 (Cervos)</div>
          </div>
          <div className="item">
            <div className="label">Test</div>
            <div>Every Ranfac "Due from" should mirror a Cervos "Due to" for the same amount. Gap indicates a missing post on one side.</div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>2 · MVP</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Ranfac due from Cervos" value={fmt(result.ranfacDueFrom)} />
        <Kpi label="Ranfac due to Cervos" value={fmt(result.ranfacDueTo)} />
        <Kpi label="Net due to Ranfac" value={fmt(result.netDueToRanfac)} />
        <Kpi
          label="Cross-company mismatch"
          value={fmt(result.crossCompanyMismatch)}
          variant={mirrored ? "ok" : "warn"}
        />
      </div>

      {!mirrored && (
        <div className="banner warn">
          Ranfac's intercompany balances do not mirror Cervos's. Review the
          pairings below — a non-zero Δ indicates an entry that's been booked
          on one side but not the other.
        </div>
      )}

      <WorkpaperActions reconId="intercompany-cervos" period={period} />

      <div className="card">
        <h2>Account pairings</h2>
        <table>
          <thead>
            <tr>
              <th>Ranfac GL</th>
              <th>Description</th>
              <th className="num">Ranfac $</th>
              <th>Cervos GL</th>
              <th>Description</th>
              <th className="num">Cervos $</th>
              <th className="num">Δ</th>
            </tr>
          </thead>
          <tbody>
            {result.pairings.map((p, i) => (
              <tr key={i} className={Math.abs(p.mismatch) > 0.01 ? "row-has-adj" : ""}>
                <td className="monoish">{p.ranfacAccount ?? "—"}</td>
                <td>{p.ranfacDescription || "—"}</td>
                <td className="num">
                  {p.ranfacAmount === 0 ? "—" : fmt(p.ranfacAmount)}
                </td>
                <td className="monoish">{p.cervosAccount ?? "—"}</td>
                <td>{p.cervosDescription || "—"}</td>
                <td className="num">
                  {p.cervosAmount === 0 ? "—" : fmt(p.cervosAmount)}
                </td>
                <td className="num">
                  {Math.abs(p.mismatch) < 0.01 ? "—" : fmt(p.mismatch)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className={`kpi ${variant ? "variance" : ""}`}>
      <div className="label">{label}</div>
      <div className={`value ${variant ?? ""}`}>{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
